import { calendarOutline, helpCircleOutline, library, peopleOutline, person, personAddOutline } from "ionicons/icons";
import homeIcon from "../assets/MentalPlus/NavbarIcons/homeicon.svg";
import videosIcon from "../assets/MentalPlus/NavbarIcons/videosicon.svg";
import helpIcon from "../assets/MentalPlus/NavbarIcons/helpicon.svg";
import evolutionIcon from "../assets/MentalPlus/NavbarIcons/evolutionicon.svg";
import { getProjectConfig } from "../projects";
import type { TabId, TabDefinition } from "../projects/types";
import { RoleEnum } from "../types/invitation.types";

/**
 * Per-study bottom-tab rules.
 *
 * The tab ROUTES in Tabs.tsx stay registered for every study; this registry only
 * decides which buttons the tab bar shows and which tab a study lands on. The
 * per-study tab LISTS live in src/projects/<slug>.ts (first id = landing tab).
 *
 * ROLE-GATED TABS: a definition may carry `roles` (e.g. suivi = investigators
 * only). Every helper takes an optional roleName; a role-gated tab is dropped
 * whenever the caller's role is unknown or not in the list — so with
 * role=undefined the fallbacks stay the role-free tabs (questionnaires/account)
 * and the landing tab can never be a page the user would be bounced out of.
 */

export type { TabId, TabDefinition };

const TAB_DEFINITIONS: Record<TabId, TabDefinition> = {
  home: { id: "home", labelKey: "mentalplus_tab_home", iconSrc: homeIcon },
  videos: { id: "videos", labelKey: "mentalplus_tab_videos", iconSrc: videosIcon },
  helps: { id: "helps", labelKey: "mentalplus_tab_help", iconSrc: helpIcon },
  evolution: {
    id: "evolution",
    labelKey: "mentalplus_tab_evolution",
    iconSrc: evolutionIcon,
  },
  account: { id: "account", labelKey: "mentalplus_tab_account", icon: person },
  questionnaires: {
    id: "questionnaires",
    labelKey: "mentalplus_tab_questionnaires",
    icon: helpCircleOutline,
    showUndoneBadge: true,
  },
  // Researcher-authored resource articles (ARTICLE contents) — cards derive from
  // each article's own blocks; opt a study in by adding "resources" to its tab list.
  resources: {
    id: "resources",
    labelKey: "resources_tab_label",
    // Filled variant (not libraryOutline) to match the other filled tab icons.
    icon: library,
  },
  // Investigator "Suivi" dashboard (subject groups / families). First
  // role-gated tab: listed first in a project's tabs, it becomes the
  // investigator LANDING tab while everyone else falls through to the next
  // role-free tab. No badge on purpose (design contract: the caption sentence
  // inside the page is the pressure gauge, the tab must not nag).
  suivi: {
    id: "suivi",
    labelKey: "suivi_tab_label",
    icon: peopleOutline,
    roles: [RoleEnum.INVESTIGATOR],
  },
  // Subject "Calendrier": agenda of open + upcoming questionnaires. Subject-only
  // (investigators get their horizon in the suivi dashboard); studies opt in via
  // their project tabs list (Outborn yes, mentalplus no).
  calendar: {
    id: "calendar",
    labelKey: "calendar_tab_label",
    icon: calendarOutline,
    roles: [RoleEnum.SUBJECT],
  },
  // Subject "Inviter": a participant invites a relative into their OWN dossier
  // (Outborn: a parent inviting the second parent). Subject-only — staff invite
  // from the Suivi dossier flow; the page itself is study-agnostic (it offers
  // whatever invitation configs the researcher authored for the subject's role).
  invitation: {
    id: "invitation",
    labelKey: "invitation_tab_label",
    icon: personAddOutline,
    roles: [RoleEnum.SUBJECT],
  },
};

const isTabAllowed = (id: TabId, roleName?: RoleEnum): boolean => {
  const roles = TAB_DEFINITIONS[id].roles;
  if (!roles) return true;
  return roleName != null && roles.includes(roleName);
};

export function getStudyTabIds(studySlug: string, roleName?: RoleEnum): TabId[] {
  // The slug can come straight from the URL (typed/bookmarked, any casing) —
  // getProjectConfig normalizes and falls back to the experience-free default.
  return getProjectConfig(studySlug).tabs.filter((id) => isTabAllowed(id, roleName));
}

export function getStudyTabs(studySlug: string, roleName?: RoleEnum): TabDefinition[] {
  return getStudyTabIds(studySlug, roleName).map((id) => TAB_DEFINITIONS[id]);
}

/** The tab a study lands on when entering the app (first ALLOWED tab of its list). */
export function getDefaultTab(studySlug: string, roleName?: RoleEnum): TabId {
  return getStudyTabIds(studySlug, roleName)[0];
}

/** Landing-tab path — replaces every redirect that used to hardcode `/tabs/home`. */
export function getDefaultTabPath(studySlug: string, roleName?: RoleEnum): string {
  return `/${studySlug}/tabs/${getDefaultTab(studySlug, roleName)}`;
}
